import { useNavigate } from "react-router-dom";

const footerLinks = [
  { label: "Home", href: "#home" },
  { label: "Our Services", href: "#services" },
  { label: "About Us", href: "#about" },
  { label: "Company Profile", href: "/company-profile" },
  { label: "Project Gallery", href: "/project-gallery" },
  { label: "Blog", href: "/blog" },
  { label: "Shop Online", href: "/store" },
  { label: "Contact Us", href: "#contact" },
];

const services = [
  "3D & 2D Design",
  "Commercial Kitchen",
  "HVAC Systems",
  "Gas & TIG Welding",
  "Plumbing",
  "Hospital Equipment",
  "Food Processing",
  "Stainless Steel Railing",
];

export default function Footer() {
  const navigate = useNavigate();

  const handleNav = (href: string) => {
    if (href.startsWith("/")) {
      navigate(href);
      return;
    }
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <footer
      className="relative overflow-hidden"
      style={{
        background: "linear-gradient(135deg, #e0f7f7 0%, #ccf3f2 40%, #d4f5ee 70%, #e8fdf8 100%)",
        borderTop: "2px solid rgba(20,184,166,0.25)",
      }}
    >
      {/* Decorative background blobs */}
      <div className="absolute -top-24 -left-24 w-80 h-80 rounded-full opacity-30 pointer-events-none"
        style={{ background: "radial-gradient(circle, rgba(6,182,212,0.35) 0%, transparent 70%)" }} />
      <div className="absolute -bottom-20 -right-20 w-72 h-72 rounded-full opacity-25 pointer-events-none"
        style={{ background: "radial-gradient(circle, rgba(20,184,166,0.35) 0%, transparent 70%)" }} />

      {/* Top band */}
      <div className="relative border-b border-teal-200/60 py-10">
        <div className="max-w-7xl mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-3">
            <img
              src="/logo.png"
              alt="ISMAN Logo"
              className="h-10 w-auto object-contain"
            />
            <div>
              <p className="text-teal-900 font-bold text-sm uppercase tracking-wider">ISMAN Company</p>
              <p className="text-teal-600 text-xs uppercase tracking-widest">Engineering Services</p>
            </div>
          </div>
          <p className="text-teal-700/70 text-sm text-center md:text-right max-w-sm">
            We provide solutions and take away your worries — Kenya&apos;s trusted engineering partner.
          </p>
        </div>
      </div>

      {/* Main footer */}
      <div className="relative max-w-7xl mx-auto px-6 py-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10">
        {/* Brand col */}
        <div>
          <h4 className="text-teal-900 font-bold text-xs uppercase tracking-widest mb-4">
            <a id="footer-about">About Us</a>
          </h4>
          <p className="text-teal-700/70 text-xs leading-relaxed">
            ISMAN Engineering Company delivers expert engineering services across Kenya — from commercial kitchen design to HVAC, welding, plumbing, and more.
          </p>
          <div className="flex gap-3 mt-5">
            {["ri-facebook-fill", "ri-twitter-x-fill", "ri-linkedin-fill", "ri-whatsapp-line"].map((icon) => (
              <a
                key={icon}
                href="https://isman.co.ke"
                target="_blank"
                rel="noopener noreferrer"
                className="w-8 h-8 flex items-center justify-center bg-teal-100 hover:bg-teal-600 rounded-md transition-colors cursor-pointer group"
              >
                <i className={`${icon} text-sm text-teal-600 group-hover:text-white`}></i>
              </a>
            ))}
          </div>
        </div>

        {/* Quick links */}
        <div>
          <h4 className="text-teal-900 font-bold text-xs uppercase tracking-widest mb-4">
            <a id="footer-links">Quick Links</a>
          </h4>
          <ul className="space-y-2">
            {footerLinks.map((link) => (
              <li key={link.label}>
                <a
                  href={link.href}
                  onClick={(e) => { e.preventDefault(); handleNav(link.href); }}
                  className="text-xs text-teal-700/70 hover:text-teal-600 transition-colors cursor-pointer flex items-center gap-1.5"
                >
                  <i className="ri-arrow-right-s-line text-teal-500"></i>
                  {link.label}
                </a>
              </li>
            ))}
          </ul>
        </div>

        {/* Services */}
        <div>
          <h4 className="text-teal-900 font-bold text-xs uppercase tracking-widest mb-4">
            <a id="footer-services">Our Services</a>
          </h4>
          <ul className="space-y-2">
            {services.map((s) => (
              <li key={s}>
                <a
                  href="#services"
                  onClick={(e) => { e.preventDefault(); handleNav("#services"); }}
                  className="text-xs text-teal-700/70 hover:text-teal-600 transition-colors cursor-pointer flex items-center gap-1.5"
                >
                  <i className="ri-arrow-right-s-line text-teal-500"></i>
                  {s}
                </a>
              </li>
            ))}
          </ul>
        </div>

        {/* Contact */}
        <div>
          <h4 className="text-teal-900 font-bold text-xs uppercase tracking-widest mb-4">
            <a id="footer-contact">Contact Info</a>
          </h4>
          <ul className="space-y-4">
            <li>
              <a href="tel:0724114555" className="flex items-start gap-3 group cursor-pointer">
                <span className="w-7 h-7 flex items-center justify-center bg-teal-100 rounded-md mt-0.5 shrink-0 group-hover:bg-teal-600 transition-colors">
                  <i className="ri-phone-line text-xs text-teal-600 group-hover:text-white"></i>
                </span>
                <span className="text-xs text-teal-700/70 group-hover:text-teal-600 transition-colors mt-1.5">072 411 4555</span>
              </a>
            </li>
            <li>
              <a href="mailto:info@isman.co.ke" className="flex items-start gap-3 group cursor-pointer">
                <span className="w-7 h-7 flex items-center justify-center bg-teal-100 rounded-md mt-0.5 shrink-0 group-hover:bg-teal-600 transition-colors">
                  <i className="ri-mail-line text-xs text-teal-600 group-hover:text-white"></i>
                </span>
                <span className="text-xs text-teal-700/70 group-hover:text-teal-600 transition-colors mt-1.5">info@isman.co.ke</span>
              </a>
            </li>
            <li className="flex items-start gap-3">
              <span className="w-7 h-7 flex items-center justify-center bg-teal-100 rounded-md mt-0.5 shrink-0">
                <i className="ri-map-pin-line text-xs text-teal-600"></i>
              </span>
              <span className="text-xs text-teal-700/70 mt-1.5">Nairobi, Kenya</span>
            </li>
          </ul>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="relative border-t border-teal-200/60 py-5">
        <div className="max-w-7xl mx-auto px-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-teal-700/60">
          <p>&copy; {new Date().getFullYear()} ISMAN Engineering Company. All rights reserved.</p>
          <div className="flex gap-4">
            <a href="https://isman.co.ke/about-us" target="_blank" rel="noopener noreferrer nofollow" className="hover:text-teal-600 transition-colors cursor-pointer">About</a>
            <a href="https://isman.co.ke/news--publications" target="_blank" rel="noopener noreferrer nofollow" className="hover:text-teal-600 transition-colors cursor-pointer">News</a>
            <a href="/project-gallery" className="hover:text-teal-600 transition-colors cursor-pointer">Gallery</a>
          </div>
        </div>
      </div>
    </footer>
  );
}
